import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { OrderHistoryItems } from '@app/core/models';
import { HelperService } from '@app/core/services';

const STATUS_MESSAGES = {
  cancelled:
    'Frame order status not available <br/> Please call customer service at 1-855-658-1705 for information.',
  contactCustomerService:
    'Please contact Customer Service for more information at 1-855-658-1705',
  invalidRx:
    'Based on the RX ID entered, we are unable to match a valid RX order to your frame order.  We will continue to search for your RX order and process it as soon as located, but this might delay delivery.  In the future, providing a valid RX ID will ensure  the fastest possible delivery. <br/><br/>Your Rx Order ID can be found in the following systems as:<br/> &nbsp;&nbsp;&nbsp; • VisionWeb: E-Order # (i.e. SP1UF1WF)<br/> &nbsp;&nbsp;&nbsp; • Eyefinity: Lab Order / Authorization # (i.e. 43509121)<br/> &nbsp;&nbsp;&nbsp;  • EyeMed: E-Order # (i.e. SP1UJ1MB)<br/><br/>Please contact Customer Service for more information at 1-855-658-1705.',
  outOfStock:
    'More frames are on the way from the vendor. Your order will be prioritized and processed as soon as possible',
  rxNotYetAvailable:
    'Rx Order information may take hours to display in our systems',
  invalidRxHours: '12',
  completeJob: 'Frame + Rx order',
  framePatient: 'Frame only order',
  frameOtherLab: 'Required managed care lab order',
  deploymentDate: '2017-11-30T14:57:00',
  frameIncorrectLabMessage: 'Frame is incorrect',
};

@Component({
  selector: 'ecp-my-orders-detail-card',
  templateUrl: './my-orders-detail-card.component.html',
  styleUrls: ['./my-orders-detail-card.component.scss'],
})
export class MyOrdersDetailCardComponent implements OnInit {
  @Input() order: OrderHistoryItems;

  /**
   * constructor
   * @param helper
   */
  constructor(private helper: HelperService) {}
  ngOnInit(): void {}

  public checkIfForBoard(name) {
    return this.helper.checkIfForBoard(name);
  }

  /**
   * checkAvailability
   * @param param
   */
  public checkAvailability(param) {
    return this.helper.checkAvailability(param);
  }

  /**
   * convertToLocalDate
   * @param date
   */
  public convertToLocalDate(date) {
    return this.helper.convertToLocalDate(date);
  }

  /**
   * getContactInfo
   * @param order
   */
  public getContactInfo(order) {
    return this.helper.getContactInfo(order);
  }

  /**
   * getFrameStatus
   * @param order
   */
  public getFrameStatus(order) {
    // ====================================
    // Checking of null and empty strings
    // Convert to null those have values
    // ====================================
    let fd_lineUseType =
      order.lineUseType === null || order.lineUseType === ''
        ? null
        : order.lineUseType.toLowerCase();
    let fd_sqlOrderStatus =
      order.sqlOrderStatus === null || order.sqlOrderStatus === ''
        ? null
        : order.sqlOrderStatus.toLowerCase();
    let fd_sqlRXStatus =
      order.sqlRXStatus === null ? '' : order.sqlRXStatus.toLowerCase();
    // let fd_SqlRXOrderNumber = order.sqlRXOrderNumber === null || order.sqlRXOrderNumber === '' ? null : order.sqlRXOrderNumber;
    let fd_RxOrderPlacedInLabID =
      order.rxOrderPlacedInLabID === null || order.rxOrderPlacedInLabID === ''
        ? null
        : order.rxOrderPlacedInLabID;
    //let fd_RxOrderPlacedInLabName =  order.rxOrderPlacedInLabName === null || order.rxOrderPlacedInLabName === ''  ? null : order.rxOrderPlacedInLabName;

    // ===========================
    // For debugging purpose only
    // ===========================
    //if (order.orderNumber === 'FR2B7KDS' || order.orderNumber === 'FR2B6DMC') {
    //    console.log(order);
    //}

    // ========================
    // For not existing in DB
    // ========================
    if (fd_lineUseType === null) {
      // Deployment date config must be set to Central Time Zone
      let deploymentDate = new Date(STATUS_MESSAGES.deploymentDate);
      let orderDate = new Date(order.orderDate);

      // =====================================================
      // To implement forPatientToLab even it is complete job
      // =====================================================
      if (orderDate < deploymentDate) {
        // For complete job
        if (
          order.lineItems[0].rxId !== null &&
          order.lineItems[0].rxId !== ''
        ) {
          if (order.status !== null)
            return { status: 'Frame order received', detail: '' };
          else return { status: '', detail: '' };
        }
        // For frame only
        else {
          return { status: 'Order received', detail: '' };
        }
      }
      // =====================================================================
      // To implement new logic for complete job to be a forPatientToPatient
      // =====================================================================
      else {
        // For complete job
        if (
          order.lineItems[0].rxId !== null &&
          order.lineItems[0].rxId !== ''
        ) {
          if ((order.lineItems[0].orderType === 'ForPatientToPatient') || (order.lineItems[0].orderType === 'ForCompleteJobMTP'))
            return { status: 'Frame order received', detail: '' };
          else if (order.lineItems[0].orderType === 'ForPatientToLab') {
            // ===========================
            // 20180425 - Marc Jason Crusina
            // To handle Vision Source Midland with ordertype as ForPatientToLab
            // ===========================
            if (this.helper.isMidland(order.dropship.name))
              return { status: 'Frame order received', detail: '' };
            else return { status: 'Order received', detail: '' };
          } else return { status: '', detail: '' };
        }
        // For frame only
        else {
          return { status: 'Order received', detail: '' };
        }
      }
    }
    // ===================================
    // For complete job or Frame with RX
    // ===================================
    else if ((fd_lineUseType === 'complete job') || (fd_lineUseType === 'complete job - dtp')) {
      // ===========================
      // Scenario 2 (Found in Elink)
      // Value is 1
      // ===========================
      if (order.rxOrderFoundInELINK === 1) {
        // Frame order received
        if (
          fd_sqlOrderStatus === null ||
          fd_sqlOrderStatus === 'ready to release' ||
          fd_sqlOrderStatus === 'book'
        ) {
          // For scenario 2.2.2 when the framestatus is null and the rx status is mail out
          if (fd_sqlRXStatus === 'mail out')
            return {
              status: 'Frame order received',
              detail: STATUS_MESSAGES.contactCustomerService,
            };
          else return { status: 'Frame order received', detail: '' };
        }
        // Frame Order Status not available ...
        else if (
          fd_sqlRXStatus === 'cancelled' &&
          fd_sqlOrderStatus !== 'cancelled'
        ) {
          if (fd_sqlOrderStatus !== 'cancelled') {
            return {
              status: 'Frame order status not available',
              detail: STATUS_MESSAGES.contactCustomerService,
            };
          } else {
            return { status: '', detail: '' };
          }
        }
        // Frame order being prepared
        else if (
          fd_sqlOrderStatus === 'released to warehouse' ||
          fd_sqlOrderStatus === 'staged/pick confirmed'
        ) {
          if (fd_sqlRXStatus === 'mail out')
            return {
              status: 'Frame order being prepared',
              detail: STATUS_MESSAGES.contactCustomerService,
            };
          else return { status: 'Frame order being prepared', detail: '' };
        } else if (fd_sqlOrderStatus === 'shipped') {
          // Display with shipped on date only
          if (fd_sqlRXStatus === 'mail out')
            return {
              status:
                'Shipped on ' +
                this.helper.convertToLocalDate(order.sqlRXOrderTimeStamp),
              detail: '',
            };
          // Display with Frame and Rx status
          else return { status: 'Frame at lab', detail: '' };
        }
        // Frame Cancelled
        else if (fd_sqlOrderStatus === 'cancelled') {
          return {
            status:
              'Frame cancelled on ' +
              this.helper.convertToLocalDate(order.sqlOrderStatusDate),
            detail: '',
          };
        }
        // Out of stock
        else if (fd_sqlOrderStatus === 'backordered') {
          return {
            status: 'Frame temporarily out of stock',
            detail: STATUS_MESSAGES.outOfStock,
          };
        } else return { status: '', detail: '' };
      }
      // ===============================
      // Scenario 3 (Not found in ELink)
      // Value is 0
      // ===============================
      else {
        // ========================
        // Placed at correct lab
        // ========================
        if (!fd_RxOrderPlacedInLabID) {
          // Frame order being prepared
          if (
            fd_sqlOrderStatus === 'released to warehouse' ||
            fd_sqlOrderStatus === 'staged/pick confirmed'
          )
            return { status: 'Frame order being prepared', detail: '' };
          // Frame order at lab or shipped
          else if (fd_sqlOrderStatus === 'shipped') {
            return {
              status: 'Frame order at lab or shipped',
              detail: STATUS_MESSAGES.contactCustomerService,
            };
          }
          // Out of stock
          else if (fd_sqlOrderStatus === 'backordered') {
            return {
              status: 'Frame temporarily out of stock',
              detail: STATUS_MESSAGES.outOfStock,
            };
          }
          // Frame Cancelled
          else if (fd_sqlOrderStatus === 'cancelled')
            return {
              status:
                'Frame cancelled on ' +
                this.helper.convertToLocalDate(order.sqlOrderStatusDate),
              detail: '',
            };
          else return { status: 'Frame order received', detail: '' };
        }
        // ========================
        // Placed at incorrect lab
        // ========================
        else {
          return {
            status: 'Frame shipping information not available',
            detail: STATUS_MESSAGES.frameIncorrectLabMessage,
          };
        }
      }
    }
    // ===============
    // For Frame Only
    // ===============
    else {
      // =================
      // Not complete job
      // =================
      if (fd_lineUseType !== 'complete job') {
        // ======================================
        // Scenario 1 (For Patient & For Board)
        // ======================================
        if (
          fd_lineUseType === 'frame only (patient)' ||
          fd_lineUseType === 'frame only (board)'
        ) {
          if (
            fd_sqlOrderStatus === null ||
            fd_sqlOrderStatus === 'ready to release' ||
            fd_sqlOrderStatus === 'book'
          )
            return { status: 'Order received', detail: '' };
          else if (
            fd_sqlOrderStatus === 'released to warehouse' ||
            fd_sqlOrderStatus === 'staged/pick confirmed' ||
            fd_sqlOrderStatus === 'ready to release'
          )
            return { status: 'Order being prepared', detail: '' };
          else if (fd_sqlOrderStatus === 'shipped') {
            // Display with shipped on date only
            return {
              status:
                'Shipped on ' +
                this.helper.convertToLocalDate(order.sqlOrderStatusDate),
              detail: '',
            };
          } else if (fd_sqlOrderStatus === 'cancelled') {
            return {
              status:
                'Cancelled on ' +
                this.helper.convertToLocalDate(order.sqlOrderStatusDate),
              detail: '',
            };
          } else if (fd_sqlOrderStatus === 'backordered') {
            return {
              status: 'Temporarily out of stock',
              detail: STATUS_MESSAGES.outOfStock,
            };
          } else return { status: '', detail: '' };
        }

        // =======================
        // Scenario 4 (Other lab)
        // =======================
        else if (
          fd_lineUseType === 'frame only (other lab)' ||
          fd_lineUseType === 'complete job - partner lab' ||
          fd_lineUseType === 'other'
        ) {
          if (
            fd_sqlOrderStatus === null ||
            fd_sqlOrderStatus === 'ready to release' ||
            fd_sqlOrderStatus === 'book'
          )
            return { status: 'Order received', detail: '' };
          else if (
            fd_sqlOrderStatus === 'released to warehouse' ||
            fd_sqlOrderStatus === 'staged/pick confirmed' ||
            fd_sqlOrderStatus === 'ready to release'
          ) {
            // ===========================
            // 20180508 - Marc Jason Crusina
            // To handle Vision Source Midland with ordertype as ForPatientToLab
            // ===========================
            if (this.helper.isMidland(order.dropship.name))
              return { status: 'Frame order being prepared', detail: '' };
            else return { status: 'Order being prepared', detail: '' };
          } else if (fd_sqlOrderStatus === 'shipped') {
            // ===========================
            // 20180508 - Marc Jason Crusina
            // To handle Vision Source Midland with ordertype as ForPatientToLab
            // ===========================
            if (this.helper.isMidland(order.dropship.name))
              return {
                status:
                  'Frame shipped to ' +
                  order.dropship.name +
                  ' on ' +
                  this.helper.convertToLocalDate(order.sqlOrderStatusDate),
                detail: '',
              };
            else
              return {
                status:
                  'Shipped to ' +
                  order.dropship.name +
                  ' on ' +
                  this.helper.convertToLocalDate(order.sqlOrderStatusDate),
                detail: '',
              }; // Frame only other lab
          } else if (fd_sqlOrderStatus === 'cancelled') {
            return {
              status:
                'Cancelled on ' +
                this.helper.convertToLocalDate(order.sqlOrderStatusDate),
              detail: '',
            };
          } else if (fd_sqlOrderStatus === 'backordered') {
            return {
              status: 'Temporarily out of stock',
              detail: STATUS_MESSAGES.outOfStock,
            };
          } else return { status: '', detail: '' };
        }
      } else return { status: '', detail: '' };
    }
  }

  /**
   * interumOrderRow
   * @param order
   */
  public interumOrderRow(order) {
    return this.helper.interumOrderRow(order);
  }

  /**
   * checkLineUseType
   * @param order
   */
  public checkLineUseType(order) {
    return this.helper.checkLineUseType(order);
  }

  /**
   * getRxStatus
   * @param order
   */
  public getRxStatus(order) {
    return this.helper.getRxStatus(order);
  }
}
